// src/theme/colorTheme.js

// Color manipulation utilities
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const lighten = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const amount = Math.round(255 * (percent / 100));
  return rgbToHex(
    Math.min(255, rgb.r + amount),
    Math.min(255, rgb.g + amount),
    Math.min(255, rgb.b + amount)
  );
};

const darken = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const amount = Math.round(255 * (percent / 100));
  return rgbToHex(
    Math.max(0, rgb.r - amount),
    Math.max(0, rgb.g - amount),
    Math.max(0, rgb.b - amount)
  );
};

const addOpacity = (hex, opacity) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const alpha = Math.round(opacity * 255).toString(16).padStart(2, '0');
  return `${hex}${alpha}`;
};

const rgbaString = (hex, opacity) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

// Static Color Scales - All manually defined for easy customization
const STATIC_COLORS = {
  // Blue color scale
  blue: {
    50: "#eff6ff",
    100: "#dbeafe", 
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#0973ba", // Primary brand blue
    600: "#0861a0",
    700: "#064f86",
    800: "#053d6c",
    900: "#042b52",
    950: "#031938",
    DEFAULT: "#0973ba"
  },
  
  // Orange color scale
  orange: {
    50: "#fef7f0",
    100: "#fdeee0",
    200: "#fbd5c0",
    300: "#f8bb9f",
    400: "#f5a17f",
    500: "#f79764", // Primary brand orange
    600: "#de8550",
    700: "#c5733c",
    800: "#ac6128",
    900: "#934f14",
    950: "#7a3d00",
    DEFAULT: "#f79764"
  },
  
  // Green color scale
  green: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e", // Primary green
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#0f2419",
    DEFAULT: "#22c55e"
  },
  
  // Red color scale
  red: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444", // Primary red
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a",
    DEFAULT: "#ef4444"
  },
  
  // Yellow color scale
  yellow: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b", // Primary yellow
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03",
    DEFAULT: "#f59e0b"
  },
  
  // Gray color scale
  gray: {
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#c3c6cb",
    300: "#a7aab2",
    400: "#8b909a",
    500: "#707682", // Primary gray
    600: "#565d6b",
    700: "#3e4555",
    800: "#262e3f",
    900: "#11192b",
    950: "#020617",
    DEFAULT: "#707682"
  },
  
  // Neutral colors
  neutral: {
    white: '#ffffff',
    black: '#000000',
  }
};

// Light Mode Base Colors
const LIGHT_BASE = {
  // Primary colors
  primary: STATIC_COLORS,
  
  // Semantic colors
  semantic: {
    success: STATIC_COLORS.green[500],
    error: STATIC_COLORS.red[500],
    warning: STATIC_COLORS.yellow[500],
    info: STATIC_COLORS.blue[500],
  },
  
  // Neutral colors
  neutral: {
    white: STATIC_COLORS.neutral.white,
    black: STATIC_COLORS.neutral.black,
    gray: STATIC_COLORS.gray,
    absolute_black: STATIC_COLORS.neutral.black,
    absolute_white: STATIC_COLORS.neutral.white,
  },
  
  // Background colors
  backgrounds: {
    primary: STATIC_COLORS.neutral.white,
    secondary: STATIC_COLORS.gray[50],
    tertiary: STATIC_COLORS.gray[100],
    card: rgbaString(STATIC_COLORS.neutral.white, 0.9),
    cardHover: STATIC_COLORS.neutral.white,
    modal: rgbaString(STATIC_COLORS.neutral.black, 0.4),
    overlay: rgbaString(STATIC_COLORS.neutral.white, 0.02),
    orange_gradient: `linear-gradient(90deg, ${STATIC_COLORS.orange[300]}, ${STATIC_COLORS.orange[500]})`,
    blue_gradient: `linear-gradient(90deg, ${STATIC_COLORS.blue[300]}, ${STATIC_COLORS.blue[500]})`,
  },
  
  // Border colors
  borders: {
    primary: STATIC_COLORS.gray[200],
    secondary: STATIC_COLORS.gray[300],
    hover: STATIC_COLORS.gray[300],
    focus: STATIC_COLORS.blue[500],
  },
  
  // Text colors
  text: {
    primary: STATIC_COLORS.gray[900],
    secondary: STATIC_COLORS.gray[600],
    tertiary: STATIC_COLORS.gray[500],
    inverse: STATIC_COLORS.neutral.white,
    brand: STATIC_COLORS.blue[600],
  },
  
  // Shadow values
  shadows: {
    sm: `0 1px 2px 0 ${rgbaString(STATIC_COLORS.gray[900], 0.05)}`,
    base: `0 1px 3px 0 ${rgbaString(STATIC_COLORS.gray[900], 0.1)}, 0 1px 2px 0 ${rgbaString(STATIC_COLORS.gray[900], 0.06)}`,
    md: `0 4px 6px -1px ${rgbaString(STATIC_COLORS.gray[900], 0.1)}, 0 2px 4px -1px ${rgbaString(STATIC_COLORS.gray[900], 0.06)}`,
    lg: `0 10px 15px -3px ${rgbaString(STATIC_COLORS.gray[900], 0.1)}, 0 4px 6px -2px ${rgbaString(STATIC_COLORS.gray[900], 0.05)}`,
    xl: `0 20px 25px -5px ${rgbaString(STATIC_COLORS.gray[900], 0.1)}, 0 10px 10px -5px ${rgbaString(STATIC_COLORS.gray[900], 0.04)}`,
    card: `0 4px 20px ${rgbaString(STATIC_COLORS.gray[900], 0.08)}`,
    cardHover: `0 8px 25px ${rgbaString(STATIC_COLORS.gray[900], 0.15)}`,
  },
};

// Dark Mode Base Colors
const DARK_BASE = {
  // Primary colors (adjusted for dark mode)
  primary: {
    blue: {
      50:  "#020617", // Near-black with subtle blue hue
      100: "#0b1a2d", // Very dark blue
      200: "#122b46", // Dark blue
      300: "#18415f", // Dark-medium blue
      400: "#1f5680", // Medium blue
      500: "#0973ba", // Brand blue
      600: "#2d8dd3", // Slightly brighter for dark mode active states
      700: "#4da6e0", // Lighter hover
      800: "#7bc0ec", // Light blue for contrast on dark bg
      900: "#b3dcf6", // Very light blue
      950: "#e0f2fe", // Lightest tint for highlights
      DEFAULT: "#2d8dd3" // Brighter default for dark mode
    },
    orange: {
      50: "#1a0f0a",   // Subtle orange-tinted dark background
      100: "#2d1811",  // Muted orange dark background
      200: "#422318",  // Warm neutral background
      300: "#5c2e1f",  // Subdued orange accent
      400: "#7c3a28",  // Muted orange for borders/secondary
      500: "#f79764",  // Base brand color
      600: "#fb923c",  // Slightly brighter orange for dark mode
      700: "#fdba74",  // Lighter orange for better contrast
      800: "#fed7aa",  // Very light orange
      900: "#feedde",  // Lightest orange
      950: "#fef7f0",  // Almost white orange
      DEFAULT: "#fb923c" // Brighter default for dark mode
    },
    green: {
      50: "#0a1f0a", // Dark green-tinted background
      100: "#14291f", // Dark green background
      200: "#1e3a2e", // Medium green background
      300: "#284b3d", // Light green background
      400: "#325c4c", // Neutral green
      500: "#22c55e", // Keep consistent
      600: "#4ade80", // Brighter green for dark mode
      700: "#86efac", // Light green
      800: "#bbf7d0", // Very light green
      900: "#dcfce7", // Lightest green
      950: "#f0fdf4", // Almost white green
      DEFAULT: "#4ade80"
    },
    red: {
      50: "#1f0a0a", // Dark red-tinted background
      100: "#2d1414", // Dark red background
      200: "#421e1e", // Medium red background
      300: "#572828", // Light red background
      400: "#6c3232", // Neutral red
      500: "#f87171", // Softer red for dark mode (fixes the harsh red issue)
      600: "#fca5a5", // Even softer red for interactive states
      700: "#fecaca", // Light red
      800: "#fee2e2", // Very light red
      900: "#fef2f2", // Lightest red
      950: "#ffffff", // White
      DEFAULT: "#f87171" // Much better red for dark mode
    },
    yellow: {
      50: "#1f1a0a", // Dark yellow-tinted background
      100: "#2d250a", // Dark warm background
      200: "#42300a", // Medium warm background
      300: "#573b0a", // Light warm background
      400: "#6c460a", // Neutral warm
      500: "#f59e0b", // Keep consistent
      600: "#fbbf24", // Brighter yellow for dark mode
      700: "#fcd34d", // Light yellow
      800: "#fde68a", // Very light yellow
      900: "#fef3c7", // Lightest yellow
      950: "#fffbeb", // Almost white yellow
      DEFAULT: "#fbbf24"
    },
    gray: {
      50: "#0f1419", // Warm dark background instead of pure inversion
      100: "#1a1f28", // Warmer dark gray
      200: "#252b37", // Medium warm gray
      300: "#303746", // Light warm gray
      400: "#3b4255", // Neutral warm gray
      500: "#707682", // Keep middle gray consistent
      600: "#8b909a", // Lighter gray for dark mode text
      700: "#a7aab2", // Light gray
      800: "#c3c6cb", // Very light gray
      900: "#e2e4e7", // Almost white gray
      950: "#f8f9fa", // Off-white instead of pure white
      DEFAULT: "#8b909a"
    },
  },
  
  // Semantic colors (lighter for dark mode)
  semantic: {
    success: STATIC_COLORS.green[400],
    error: STATIC_COLORS.red[400],
    warning: STATIC_COLORS.yellow[400],
    info: STATIC_COLORS.blue[400],
  },
  
  // Neutral colors (inverted)
  neutral: {
    white: STATIC_COLORS.neutral.black,
    black: STATIC_COLORS.neutral.white,
    gray: {
      50: STATIC_COLORS.gray[950],
      100: STATIC_COLORS.gray[900],
      200: STATIC_COLORS.gray[800],
      300: STATIC_COLORS.gray[700],
      400: STATIC_COLORS.gray[600],
      500: STATIC_COLORS.gray[500],
      600: STATIC_COLORS.gray[400],
      700: STATIC_COLORS.gray[300],
      800: STATIC_COLORS.gray[200],
      900: STATIC_COLORS.gray[100],
      950: STATIC_COLORS.gray[50],
      DEFAULT: STATIC_COLORS.gray[400]
    },
    absolute_black: STATIC_COLORS.neutral.black,
    absolute_white: STATIC_COLORS.neutral.white,
  },
  
  // Background colors (dark variants)
  backgrounds: {
    primary: STATIC_COLORS.gray[900],
    secondary: STATIC_COLORS.gray[800],
    tertiary: STATIC_COLORS.gray[700],
    card: rgbaString(STATIC_COLORS.neutral.white, 0.05),
    cardHover: rgbaString(STATIC_COLORS.neutral.white, 0.08),
    modal: rgbaString(STATIC_COLORS.neutral.white, 0.2),
    overlay: rgbaString(STATIC_COLORS.neutral.white, 0.02),
    orange_gradient: `linear-gradient(90deg, ${STATIC_COLORS.orange[300]}, ${STATIC_COLORS.orange[400]})`,
    blue_gradient: `linear-gradient(90deg, ${STATIC_COLORS.blue[300]}, ${STATIC_COLORS.blue[400]})`,
  },
  
  // Border colors (dark variants)
  borders: {
    primary: rgbaString(STATIC_COLORS.neutral.white, 0.1),
    secondary: rgbaString(STATIC_COLORS.neutral.white, 0.15),
    hover: rgbaString(STATIC_COLORS.neutral.white, 0.2),
    focus: STATIC_COLORS.blue[400],
  },
  
  // Text colors (dark variants)
  text: {
    primary: STATIC_COLORS.gray[50],
    secondary: STATIC_COLORS.gray[300],
    tertiary: STATIC_COLORS.gray[400],
    inverse: STATIC_COLORS.gray[900],
    brand: STATIC_COLORS.blue[400],
  },
  
  // Shadow values (dark variants)
  shadows: {
    sm: `0 1px 2px 0 ${rgbaString(STATIC_COLORS.neutral.black, 0.3)}`,
    base: `0 1px 3px 0 ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}, 0 1px 2px 0 ${rgbaString(STATIC_COLORS.neutral.black, 0.3)}`,
    md: `0 4px 6px -1px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}, 0 2px 4px -1px ${rgbaString(STATIC_COLORS.neutral.black, 0.3)}`,
    lg: `0 10px 15px -3px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}, 0 4px 6px -2px ${rgbaString(STATIC_COLORS.neutral.black, 0.3)}`,
    xl: `0 20px 25px -5px ${rgbaString(STATIC_COLORS.neutral.black, 0.5)}, 0 10px 10px -5px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}`,
    card: `0 4px 20px ${rgbaString(STATIC_COLORS.neutral.black, 0.3)}`,
    cardHover: `0 8px 25px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}`,
  },
};

// Static Dashboard Colors for Light Mode
const LIGHT_DASHBOARD = {
  // Card backgrounds
  card: {
    background: rgbaString(STATIC_COLORS.neutral.white, 0.9),
    backgroundHover: STATIC_COLORS.neutral.white,
    border: STATIC_COLORS.gray[200],
    borderHover: STATIC_COLORS.gray[300],
    shadow: `0 4px 20px ${rgbaString(STATIC_COLORS.gray[900], 0.08)}`,
    shadowHover: `0 8px 25px ${rgbaString(STATIC_COLORS.gray[900], 0.15)}`,
  },
  
  // Review Questions
  review: {
    itemBackground: STATIC_COLORS.gray[50],
    itemBackgroundHover: STATIC_COLORS.gray[100],
    badge: {
      all: {
        background: rgbaString(STATIC_COLORS.blue[500], 0.1),
        text: STATIC_COLORS.blue[700],
        border: rgbaString(STATIC_COLORS.blue[500], 0.3),
      },
      correct: {
        background: rgbaString(STATIC_COLORS.green[500], 0.1),
        text: STATIC_COLORS.green[700],
        border: rgbaString(STATIC_COLORS.green[500], 0.3),
      },
      incorrect: {
        background: rgbaString(STATIC_COLORS.red[500], 0.1),
        text: STATIC_COLORS.red[700],
        border: rgbaString(STATIC_COLORS.red[500], 0.3),
      },
      unanswered: {
        background: rgbaString(STATIC_COLORS.yellow[500], 0.1),
        text: STATIC_COLORS.yellow[700],
        border: rgbaString(STATIC_COLORS.yellow[500], 0.3),
      },
    },
  },
  
  // Domain Performance
  domain: {
    cardBackground: STATIC_COLORS.gray[50],
    cardBackgroundHover: STATIC_COLORS.neutral.white,
    progressTrack: STATIC_COLORS.gray[200],
    progress: {
      critical: `linear-gradient(90deg, ${STATIC_COLORS.red[500]}, ${STATIC_COLORS.red[400]})`,
      warning: `linear-gradient(90deg, ${STATIC_COLORS.yellow[500]}, ${STATIC_COLORS.yellow[400]})`,
      success: `linear-gradient(90deg, ${STATIC_COLORS.green[500]}, ${STATIC_COLORS.green[400]})`,
    },
    border: {
      critical: STATIC_COLORS.red[500],
      warning: STATIC_COLORS.yellow[500],
      success: STATIC_COLORS.green[500],
    },
  },
  
  // Score Circle
  score: {
    background: STATIC_COLORS.gray[100],
    gradient: `linear-gradient(90deg, ${STATIC_COLORS.red[500]}, ${STATIC_COLORS.red[400]})`,
    dropShadow: rgbaString(STATIC_COLORS.red[500], 0.2),
  },
  
  // Buttons
  button: {
    primary: {
      background: `linear-gradient(135deg, ${STATIC_COLORS.blue[500]} 0%, ${STATIC_COLORS.blue[600]} 100%)`,
      shadow: `0 4px 15px ${rgbaString(STATIC_COLORS.blue[500], 0.3)}`,
      shadowHover: `0 8px 25px ${rgbaString(STATIC_COLORS.blue[500], 0.4)}`,
    },
    secondary: {
      background: STATIC_COLORS.neutral.white,
      border: STATIC_COLORS.gray[200],
      borderHover: STATIC_COLORS.gray[300],
      text: STATIC_COLORS.gray[600],
      textHover: STATIC_COLORS.gray[900],
      shadow: `0 1px 2px 0 ${rgbaString(STATIC_COLORS.gray[900], 0.05)}`,
      shadowHover: `0 4px 6px -1px ${rgbaString(STATIC_COLORS.gray[900], 0.1)}`,
    },
  },
  
  // Status colors
  status: {
    correct: STATIC_COLORS.green[500],
    incorrect: STATIC_COLORS.red[500],
    unanswered: STATIC_COLORS.yellow[500],
    primary: STATIC_COLORS.blue[600],
  },
};

// Static Dashboard Colors for Dark Mode
const DARK_DASHBOARD = {
  // Card backgrounds
  card: {
    background: rgbaString(STATIC_COLORS.neutral.white, 0.05),
    backgroundHover: rgbaString(STATIC_COLORS.neutral.white, 0.08),
    border: rgbaString(STATIC_COLORS.neutral.white, 0.1),
    borderHover: rgbaString(STATIC_COLORS.neutral.white, 0.2),
    shadow: `0 4px 20px ${rgbaString(STATIC_COLORS.neutral.black, 0.3)}`,
    shadowHover: `0 8px 25px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}`,
  },
  
  // Review Questions
  review: {
    itemBackground: STATIC_COLORS.gray[800],
    itemBackgroundHover: STATIC_COLORS.gray[700],
    badge: {
      all: {
        background: rgbaString(STATIC_COLORS.blue[400], 0.1),
        text: STATIC_COLORS.blue[400],
        border: rgbaString(STATIC_COLORS.blue[400], 0.3),
      },
      correct: {
        background: rgbaString(STATIC_COLORS.green[400], 0.1),
        text: STATIC_COLORS.green[400],
        border: rgbaString(STATIC_COLORS.green[400], 0.3),
      },
      incorrect: {
        background: rgbaString(STATIC_COLORS.red[400], 0.1),
        text: STATIC_COLORS.red[400],
        border: rgbaString(STATIC_COLORS.red[400], 0.3),
      },
      unanswered: {
        background: rgbaString(STATIC_COLORS.yellow[400], 0.1),
        text: STATIC_COLORS.yellow[400],
        border: rgbaString(STATIC_COLORS.yellow[400], 0.3),
      },
    },
  },
  
  // Domain Performance
  domain: {
    cardBackground: STATIC_COLORS.gray[800],
    cardBackgroundHover: rgbaString(STATIC_COLORS.neutral.white, 0.08),
    progressTrack: rgbaString(STATIC_COLORS.neutral.white, 0.1),
    progress: {
      critical: `linear-gradient(90deg, ${STATIC_COLORS.red[400]}, ${STATIC_COLORS.red[300]})`,
      warning: `linear-gradient(90deg, ${STATIC_COLORS.yellow[400]}, ${STATIC_COLORS.yellow[300]})`,
      success: `linear-gradient(90deg, ${STATIC_COLORS.green[400]}, ${STATIC_COLORS.green[300]})`,
    },
    border: {
      critical: STATIC_COLORS.red[400],
      warning: STATIC_COLORS.yellow[400],
      success: STATIC_COLORS.green[400],
    },
  },
  
  // Score Circle
  score: {
    background: rgbaString(STATIC_COLORS.neutral.white, 0.1),
    gradient: `linear-gradient(90deg, ${STATIC_COLORS.red[400]}, ${STATIC_COLORS.red[300]})`,
    dropShadow: rgbaString(STATIC_COLORS.red[400], 0.3),
  },
  
  // Buttons
  button: {
    primary: {
      background: `linear-gradient(135deg, ${STATIC_COLORS.blue[400]} 0%, ${STATIC_COLORS.blue[500]} 100%)`,
      shadow: `0 4px 15px ${rgbaString(STATIC_COLORS.blue[400], 0.3)}`,
      shadowHover: `0 8px 25px ${rgbaString(STATIC_COLORS.blue[400], 0.4)}`,
    },
    secondary: {
      background: rgbaString(STATIC_COLORS.neutral.white, 0.05),
      border: rgbaString(STATIC_COLORS.neutral.white, 0.1),
      borderHover: rgbaString(STATIC_COLORS.neutral.white, 0.15),
      text: STATIC_COLORS.gray[300],
      textHover: STATIC_COLORS.gray[50],
      shadow: `0 1px 2px 0 ${rgbaString(STATIC_COLORS.neutral.black, 0.3)}`,
      shadowHover: `0 4px 6px -1px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}`,
    },
  },
  
  // Status colors
  status: {
    correct: STATIC_COLORS.green[400],
    incorrect: STATIC_COLORS.red[400],
    unanswered: STATIC_COLORS.yellow[400],
    primary: STATIC_COLORS.blue[400],
  },
};

// Light Theme
export const LIGHT_THEME = {
  // Spread all base colors
  ...LIGHT_BASE,
  
  // Legacy color mappings
  white: LIGHT_BASE.neutral.white,
  black: LIGHT_BASE.neutral.black,
  gray: LIGHT_BASE.neutral.gray,
  green: STATIC_COLORS.green,
  red: STATIC_COLORS.red,
  yellow: STATIC_COLORS.yellow,
  
  // Hero background
  hero_background: {
    0: STATIC_COLORS.blue[50],
    15: STATIC_COLORS.blue[100],
    30: lighten(STATIC_COLORS.blue[200], 5),
    45: STATIC_COLORS.blue[200],
    60: lighten(STATIC_COLORS.blue[300], 5),
    75: STATIC_COLORS.blue[300],
    90: lighten(STATIC_COLORS.blue[400], 5),
    100: STATIC_COLORS.blue[400]
  },
  
  // Navbar test
  navbar_test: {
    background: STATIC_COLORS.blue[500],
    text: STATIC_COLORS.neutral.black,
    progress_bar: STATIC_COLORS.blue[200],
    progress_bar_fill: STATIC_COLORS.blue[400],
    dot_color: STATIC_COLORS.neutral.black,
    end_button: STATIC_COLORS.red[500],
    end_button_hover: STATIC_COLORS.red[200],
    end_button_text: STATIC_COLORS.neutral.black,
    arrow_background: STATIC_COLORS.orange[500],
  },
  
  // Text
  text: LIGHT_BASE.text,

  // Button Colors (Light Mode)
  buttons: {
    // Primary button - Blue gradient
    primary: {
      background: `linear-gradient(135deg, ${STATIC_COLORS.blue[500]} 0%, ${STATIC_COLORS.blue[600]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${STATIC_COLORS.blue[600]} 0%, ${STATIC_COLORS.blue[700]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${STATIC_COLORS.blue[700]} 0%, ${STATIC_COLORS.blue[800]} 100%)`,
      backgroundDisabled: STATIC_COLORS.gray[300],
      text: STATIC_COLORS.neutral.white,
      textDisabled: STATIC_COLORS.gray[500],
      border: STATIC_COLORS.blue[600],
      borderHover: STATIC_COLORS.blue[700],
      shadow: `0 4px 12px ${rgbaString(STATIC_COLORS.blue[600], 0.3)}`,
      shadowHover: `0 6px 20px ${rgbaString(STATIC_COLORS.blue[600], 0.4)}`,
    },
    
    // Secondary button - Orange gradient
    secondary: {
      background: `linear-gradient(135deg, ${STATIC_COLORS.orange[400]} 0%, ${STATIC_COLORS.orange[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${STATIC_COLORS.orange[500]} 0%, ${STATIC_COLORS.orange[600]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${STATIC_COLORS.orange[600]} 0%, ${STATIC_COLORS.orange[700]} 100%)`,
      backgroundDisabled: STATIC_COLORS.gray[300],
      text: STATIC_COLORS.neutral.white,
      textDisabled: STATIC_COLORS.gray[500],
      border: STATIC_COLORS.orange[500],
      borderHover: STATIC_COLORS.orange[600],
      shadow: `0 4px 12px ${rgbaString(STATIC_COLORS.orange[500], 0.3)}`,
      shadowHover: `0 6px 20px ${rgbaString(STATIC_COLORS.orange[500], 0.4)}`,
    },
    
    // Outline Blue button
    outlinePrimary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${STATIC_COLORS.blue[50]} 0%, ${STATIC_COLORS.blue[100]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${STATIC_COLORS.blue[100]} 0%, ${STATIC_COLORS.blue[200]} 100%)`,
      backgroundDisabled: 'transparent',
      text: STATIC_COLORS.blue[600],
      textHover: STATIC_COLORS.blue[700],
      textDisabled: STATIC_COLORS.gray[400],
      border: STATIC_COLORS.blue[300],
      borderHover: STATIC_COLORS.blue[400],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(STATIC_COLORS.blue[600], 0.1)}`,
    },
    
    // Outline Orange button
    outlineSecondary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${STATIC_COLORS.orange[50]} 0%, ${STATIC_COLORS.orange[100]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${STATIC_COLORS.orange[100]} 0%, ${STATIC_COLORS.orange[200]} 100%)`,
      backgroundDisabled: 'transparent',
      text: STATIC_COLORS.orange[600],
      textHover: STATIC_COLORS.orange[700],
      textDisabled: STATIC_COLORS.gray[400],
      border: STATIC_COLORS.orange[300],
      borderHover: STATIC_COLORS.orange[400],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(STATIC_COLORS.orange[500], 0.1)}`,
    },
    
    // Button sizes
    sizes: {
      sm: {
        padding: '0.5rem 1rem',
        fontSize: '0.875rem',
        borderRadius: '0.5rem',
      },
      md: {
        padding: '0.75rem 1.5rem',
        fontSize: '1rem',
        borderRadius: '0.75rem',
      },
      lg: {
        padding: '1rem 2rem',
        fontSize: '1.125rem',
        borderRadius: '0.75rem',
      }
    }
  },
  
  // Backgrounds
  background: {
    main: STATIC_COLORS.neutral.white,
    primary: STATIC_COLORS.neutral.white,
    secondary: STATIC_COLORS.gray[50],
    tertiary: STATIC_COLORS.gray[100],
    orange: rgbaString(STATIC_COLORS.orange[500], 0.15),
    blue: STATIC_COLORS.blue[50],
    input: STATIC_COLORS.gray[50],
    card: STATIC_COLORS.gray[50],
    separator: STATIC_COLORS.gray[200],
    modal: LIGHT_BASE.backgrounds.modal,
    premium: lighten(STATIC_COLORS.orange[100], 10),
    orange_gradient: LIGHT_BASE.backgrounds.orange_gradient,
    blue_gradient: LIGHT_BASE.backgrounds.blue_gradient,
  },
  
  // UI Elements
  ui: {
    primary: STATIC_COLORS.blue[500],
    secondary: STATIC_COLORS.orange[500],
    success: LIGHT_BASE.semantic.success,
    warning: LIGHT_BASE.semantic.warning,
    error: LIGHT_BASE.semantic.error,
    border: LIGHT_BASE.borders.primary,
  },
  
  // Features
  features: {
    study: {
      background: STATIC_COLORS.blue[50],
      icon: STATIC_COLORS.blue[500],
    },
    practice: {
      background: STATIC_COLORS.yellow[50],
      icon: STATIC_COLORS.yellow[600],
    },
    flashcard: {
      background: STATIC_COLORS.green[50],
      icon: STATIC_COLORS.green[600],
    },
    custom: {
      background: STATIC_COLORS.red[50],
      icon: STATIC_COLORS.red[600],
    },
  },

  dashboard: LIGHT_DASHBOARD,
  
  // Legacy mappings (for backward compatibility)
  THEME_DARK: STATIC_COLORS.blue[500],
  THEME_LIGHT: STATIC_COLORS.blue[100],
  THEME_TEXT_BLACK: STATIC_COLORS.neutral.black,
  THEME_TEXT_BLACK_LIGHT: LIGHT_BASE.text.secondary,
  THEME_TEXT_GRAY: STATIC_COLORS.gray[500],
  NORMAL_COLOR_WHITE: STATIC_COLORS.neutral.white,
  THEME_SCREEN_BACKGROUND_COLOR: LIGHT_BASE.backgrounds.primary,
  THEME_INPUT_BACKGROUND: STATIC_COLORS.gray[50],
  THEME_SEPERATER: STATIC_COLORS.gray[200],
  THEME_SCORE_CONTAINER: STATIC_COLORS.orange[500],
  THEME_PREMIUM_BACKGROUND: lighten(STATIC_COLORS.orange[100], 10),
  THEME_STUDY_QUESTION_BACKGROUND: STATIC_COLORS.blue[50],
  THEME_PRACTICE_TEST_BACKGROUND: STATIC_COLORS.yellow[50],
  THEME_FLASHCARD_BACKGROUND: STATIC_COLORS.green[50],
  THEME_CUSTOM_TEST_BACKGROUND: STATIC_COLORS.red[50],
  THEME_STUDY_ICON_BACKGROUND_COLOR: STATIC_COLORS.blue[500],
  THEME_PRACTICE_TEST_ICON_BACKGROUND_COLOR: STATIC_COLORS.yellow[600],
  THEME_FLASHCARD_ICON_BACKGROUND_COLOR: STATIC_COLORS.green[600],
  THEME_CUSTOM_ICON_BACKGROUND_COLOR: STATIC_COLORS.red[600],
};

// Dark Theme
export const DARK_THEME = {
  // Spread all base colors
  ...DARK_BASE,
  
  // Legacy color mappings
  white: DARK_BASE.neutral.white,
  black: DARK_BASE.neutral.black,
  gray: DARK_BASE.neutral.gray,
  green: DARK_BASE.primary.green,
  red: DARK_BASE.primary.red,
  yellow: DARK_BASE.primary.yellow,
  
  // Hero background
  hero_background: {
    0: DARK_BASE.primary.blue[200],
    15: DARK_BASE.primary.blue[300],
    30: DARK_BASE.primary.blue[400],
    45: DARK_BASE.primary.blue[500],
    60: DARK_BASE.primary.blue[600],
    75: DARK_BASE.primary.blue[700],
    90: DARK_BASE.primary.blue[800],
    100: DARK_BASE.primary.blue[900]
  },
  
  // Navbar test
  navbar_test: {
    background: STATIC_COLORS.blue[500],
    text: DARK_BASE.neutral.black,
    progress_bar: DARK_BASE.primary.blue[200],
    progress_bar_fill: DARK_BASE.primary.blue[400],
    dot_color: DARK_BASE.neutral.black,
    end_button: DARK_BASE.primary.red[400],
    end_button_hover: DARK_BASE.primary.red[700],
    end_button_text: DARK_BASE.neutral.black,
    arrow_background: STATIC_COLORS.orange[500],
  },
  
  // Text
  text: DARK_BASE.text,

  buttons: {
    // Primary button - Blue gradient
    primary: {
      background: `linear-gradient(135deg, ${DARK_BASE.primary.blue[400]} 0%, ${DARK_BASE.primary.blue[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${DARK_BASE.primary.blue[300]} 0%, ${DARK_BASE.primary.blue[400]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${DARK_BASE.primary.blue[500]} 0%, ${DARK_BASE.primary.blue[600]} 100%)`,
      backgroundDisabled: DARK_BASE.primary.gray[700],
      text: DARK_BASE.neutral.white,
      textDisabled: DARK_BASE.primary.gray[500],
      border: DARK_BASE.primary.blue[500],
      borderHover: DARK_BASE.primary.blue[400],
      shadow: `0 4px 12px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}`,
      shadowHover: `0 6px 20px ${rgbaString(DARK_BASE.primary.blue[500], 0.5)}`,
    },
    
    // Secondary button - Orange gradient
    secondary: {
      background: `linear-gradient(135deg, ${DARK_BASE.primary.orange[400]} 0%, ${DARK_BASE.primary.orange[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${DARK_BASE.primary.orange[300]} 0%, ${DARK_BASE.primary.orange[400]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${DARK_BASE.primary.orange[500]} 0%, ${DARK_BASE.primary.orange[600]} 100%)`,
      backgroundDisabled: DARK_BASE.primary.gray[700],
      text: DARK_BASE.neutral.white,
      textDisabled: DARK_BASE.primary.gray[500],
      border: DARK_BASE.primary.orange[500],
      borderHover: DARK_BASE.primary.orange[400],
      shadow: `0 4px 12px ${rgbaString(STATIC_COLORS.neutral.black, 0.4)}`,
      shadowHover: `0 6px 20px ${rgbaString(DARK_BASE.primary.orange[500], 0.5)}`,
    },
    
    // Outline Blue button
    outlinePrimary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${rgbaString(DARK_BASE.primary.blue[500], 0.1)} 0%, ${rgbaString(DARK_BASE.primary.blue[400], 0.15)} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${rgbaString(DARK_BASE.primary.blue[500], 0.2)} 0%, ${rgbaString(DARK_BASE.primary.blue[400], 0.25)} 100%)`,
      backgroundDisabled: 'transparent',
      text: DARK_BASE.primary.blue[400],
      textHover: DARK_BASE.primary.blue[300],
      textDisabled: DARK_BASE.primary.gray[600],
      border: DARK_BASE.primary.blue[600],
      borderHover: DARK_BASE.primary.blue[500],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(DARK_BASE.primary.blue[500], 0.2)}`,
    },
    
    // Outline Orange button
    outlineSecondary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${rgbaString(DARK_BASE.primary.orange[500], 0.1)} 0%, ${rgbaString(DARK_BASE.primary.orange[400], 0.15)} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${rgbaString(DARK_BASE.primary.orange[500], 0.2)} 0%, ${rgbaString(DARK_BASE.primary.orange[400], 0.25)} 100%)`,
      backgroundDisabled: 'transparent',
      text: DARK_BASE.primary.orange[400],
      textHover: DARK_BASE.primary.orange[300],
      textDisabled: DARK_BASE.primary.gray[600],
      border: DARK_BASE.primary.orange[600],
      borderHover: DARK_BASE.primary.orange[500],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(DARK_BASE.primary.orange[500], 0.2)}`,
    },
    
    // Button sizes
    sizes: {
      sm: {
        padding: '0.5rem 1rem',
        fontSize: '0.875rem',
        borderRadius: '0.5rem',
      },
      md: {
        padding: '0.75rem 1.5rem',
        fontSize: '1rem',
        borderRadius: '0.75rem',
      },
      lg: {
        padding: '1rem 2rem',
        fontSize: '1.125rem',
        borderRadius: '0.75rem',
      }
    }
  },
  
  // Backgrounds
  background: {
    main: STATIC_COLORS.neutral.black,
    primary: STATIC_COLORS.neutral.black,
    secondary: STATIC_COLORS.gray[900],
    tertiary: STATIC_COLORS.gray[800],
    orange: rgbaString(STATIC_COLORS.orange[500], .25),
    blue: rgbaString(STATIC_COLORS.blue[500], 0.4),
    input: DARK_BASE.backgrounds.secondary,
    card: STATIC_COLORS.gray[800],
    separator: DARK_BASE.backgrounds.tertiary,
    modal: DARK_BASE.backgrounds.modal,
    premium: darken(DARK_BASE.primary.orange[100], 10),
    orange_gradient: DARK_BASE.backgrounds.orange_gradient,
    blue_gradient: DARK_BASE.backgrounds.blue_gradient,
  },
  
  // UI Elements
  ui: {
    primary: STATIC_COLORS.blue[500],
    secondary: STATIC_COLORS.orange[500],
    success: DARK_BASE.semantic.success,
    warning: DARK_BASE.semantic.warning,
    error: DARK_BASE.semantic.error,
    border: DARK_BASE.borders.primary,
  },
  
  // Features
  features: {
    study: {
      background: DARK_BASE.backgrounds.secondary,
      icon: DARK_BASE.primary.blue[400],
    },
    practice: {
      background: DARK_BASE.backgrounds.secondary,
      icon: DARK_BASE.primary.yellow[400],
    },
    flashcard: {
      background: DARK_BASE.backgrounds.secondary,
      icon: DARK_BASE.primary.green[400],
    },
    custom: {
      background: DARK_BASE.backgrounds.secondary,
      icon: DARK_BASE.primary.red[400],
    },
  },

  dashboard: DARK_DASHBOARD,
  
  // Legacy mappings (for backward compatibility)
  THEME_DARK: STATIC_COLORS.blue[500],
  THEME_LIGHT: DARK_BASE.primary.blue[100],
  THEME_TEXT_BLACK: DARK_BASE.neutral.black,
  THEME_TEXT_BLACK_LIGHT: DARK_BASE.text.secondary,
  THEME_TEXT_GRAY: DARK_BASE.primary.gray[500],
  NORMAL_COLOR_WHITE: DARK_BASE.neutral.white,
  THEME_SCREEN_BACKGROUND_COLOR: DARK_BASE.backgrounds.primary,
  THEME_INPUT_BACKGROUND: DARK_BASE.backgrounds.secondary,
  THEME_SEPERATER: DARK_BASE.backgrounds.tertiary,
  THEME_SCORE_CONTAINER: STATIC_COLORS.orange[500],
  THEME_PREMIUM_BACKGROUND: DARK_BASE.primary.gray[800],
  THEME_STUDY_QUESTION_BACKGROUND: DARK_BASE.backgrounds.secondary,
  THEME_PRACTICE_TEST_BACKGROUND: DARK_BASE.backgrounds.secondary,
  THEME_FLASHCARD_BACKGROUND: DARK_BASE.backgrounds.secondary,
  THEME_CUSTOM_TEST_BACKGROUND: DARK_BASE.backgrounds.secondary,
  THEME_STUDY_ICON_BACKGROUND_COLOR: DARK_BASE.primary.blue[600],
  THEME_PRACTICE_TEST_ICON_BACKGROUND_COLOR: DARK_BASE.primary.yellow[600],
  THEME_FLASHCARD_ICON_BACKGROUND_COLOR: DARK_BASE.primary.green[600],
  THEME_CUSTOM_ICON_BACKGROUND_COLOR: DARK_BASE.primary.red[600],
};

// For ThemeContext Provider
export function getColorTheme(mode) {
  return mode === 'light' ? LIGHT_THEME : DARK_THEME;
}

// Export utilities and colors for direct access
export { 
  STATIC_COLORS,
  LIGHT_BASE, 
  DARK_BASE, 
  lighten, 
  darken, 
  addOpacity, 
  rgbaString
};